import { useState, useEffect, useRef } from 'react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { Outcome } from '../types';
import { Modal } from '../components/common/Modal';
import { FileUpload } from '../components/common/FileUpload';
import { Target, Pencil, Trash2 } from 'lucide-react';
import { useT } from '../i18n';

export function OutcomesPage() {
  const { t } = useT();
  const { isAdmin } = useAuth();
  const [outcomes, setOutcomes] = useState<Outcome[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [editingOutcome, setEditingOutcome] = useState<Outcome | null>(null);
  const [filterMotivatedBy, setFilterMotivatedBy] = useState<string>('');

  const [form, setForm] = useState({
    title: '',
    description: '',
    motivated_by: '',
    motivated_for: '',
    tags: [] as string[],
  });
  const [tagInput, setTagInput] = useState('');
  const tagInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadOutcomes();
  }, []);

  const loadOutcomes = async () => {
    try {
      const data = await api.getOutcomes() as Outcome[];
      setOutcomes(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingOutcome) {
        await api.updateOutcome(editingOutcome.id, form);
      } else {
        await api.createOutcome(form);
      }
      setShowModal(false);
      setEditingOutcome(null);
      setForm({ title: '', description: '', motivated_by: '', motivated_for: '', tags: [] });
      setTagInput('');
      loadOutcomes();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleEdit = (outcome: Outcome) => {
    setEditingOutcome(outcome);
    setForm({
      title: outcome.title,
      description: outcome.description,
      motivated_by: outcome.motivated_by,
      motivated_for: outcome.motivated_for,
      tags: outcome.tags || [],
    });
    setTagInput('');
    setShowModal(true);
  };

  const handleDelete = async (outcome: Outcome) => {
    if (!confirm(t('out.confirmDelete').replace('{title}', outcome.title))) return;
    try {
      await api.deleteOutcome(outcome.id);
      loadOutcomes();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleUpload = async (data: any[]) => {
    try {
      const result = await api.uploadOutcomes(data) as { success: boolean; inserted: number; errors: any[] };
      if (result.errors.length > 0) {
        alert(t('out.uploadResult').replace('{n}', String(result.inserted)).replace('{e}', String(result.errors.length)));
      }
      setShowUpload(false);
      loadOutcomes();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const addFormTag = () => {
    const tag = tagInput.trim();
    if (tag && !form.tags.includes(tag)) {
      setForm({ ...form, tags: [...form.tags, tag] });
    }
    setTagInput('');
  };

  const removeFormTag = (tag: string) => {
    setForm({ ...form, tags: form.tags.filter((t) => t !== tag) });
  };

  const motivatedByValues = [...new Set(outcomes.map((o) => o.motivated_by).filter(Boolean))].sort();
  const motivatedForValues = [...new Set(outcomes.map((o) => o.motivated_for).filter(Boolean))].sort();

  const filteredOutcomes = outcomes.filter((o) => {
    if (filterMotivatedBy && o.motivated_by !== filterMotivatedBy) return false;
    return true;
  });

  // Group by motivated_by
  const groupedByMotivatedBy = motivatedByValues.reduce((acc, mb) => {
    acc[mb] = filteredOutcomes.filter((o) => o.motivated_by === mb);
    return acc;
  }, {} as Record<string, Outcome[]>);

  // Outcomes without motivated_by
  const ungrouped = filteredOutcomes.filter((o) => !o.motivated_by);

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height: 200 }}>
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div>
      <div className="card-header mb-16">
        <div className="flex gap-8">
          <select
            className="form-select"
            style={{ width: 'auto' }}
            value={filterMotivatedBy}
            onChange={(e) => setFilterMotivatedBy(e.target.value)}
          >
            <option value="">{t('out.allMotivatedBy')}</option>
            {motivatedByValues.map((mb) => (
              <option key={mb} value={mb}>
                {mb}
              </option>
            ))}
          </select>
        </div>
        {isAdmin && (
          <div className="flex gap-8">
            <button className="btn btn-ghost" onClick={() => setShowUpload(true)}>
              {t('csv.uploadCsv')}
            </button>
            <button
              className="btn btn-accent"
              onClick={() => {
                setEditingOutcome(null);
                setForm({ title: '', description: '', motivated_by: '', motivated_for: '', tags: [] });
                setTagInput('');
                setShowModal(true);
              }}
            >
              {t('out.addOutcome')}
            </button>
          </div>
        )}
      </div>

      {filteredOutcomes.length === 0 ? (
        <div className="empty-state-card">
          <div className="empty-state">
            <div className="empty-state-icon"><Target /></div>
            <div className="empty-state-title">{t('out.emptyTitle')}</div>
            <div className="empty-state-desc">
              {isAdmin ? t('out.emptyDescAdmin') : t('out.emptyDescUser')}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-16">
          {motivatedByValues.map((mb) => {
            const mbOutcomes = groupedByMotivatedBy[mb];
            if (!mbOutcomes || mbOutcomes.length === 0) return null;

            return (
              <div key={mb} className="card">
                <h3 className="mb-12">{mb}</h3>
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('out.colTitle')}</th>
                      <th>{t('out.colMotivatedFor')}</th>
                      <th>{t('out.colTags')}</th>
                      <th>{t('out.colDescription')}</th>
                      {isAdmin && <th></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {mbOutcomes.map((outcome) => (
                      <tr key={outcome.id}>
                        <td className="font-medium">{outcome.title}</td>
                        <td>{outcome.motivated_for}</td>
                        <td>
                          {outcome.tags.length > 0 ? (
                            <div className="flex gap-4" style={{ flexWrap: 'wrap' }}>
                              {outcome.tags.map((tag) => (
                                <span key={tag} className="badge">{tag}</span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td className="text-muted">
                          {outcome.description?.slice(0, 100)}
                          {outcome.description?.length > 100 ? '...' : ''}
                        </td>
                        {isAdmin && (
                          <td>
                            <div className="flex gap-8">
                              <button className="btn-icon" onClick={() => handleEdit(outcome)} title={t('common.edit')}>
                                <Pencil className="icon-sm" />
                              </button>
                              <button className="btn-icon" onClick={() => handleDelete(outcome)} title={t('common.delete')}>
                                <Trash2 className="icon-sm" />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
          {ungrouped.length > 0 && (
            <div className="card">
              <h3 className="mb-12">{t('out.uncategorized')}</h3>
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('out.colTitle')}</th>
                    <th>{t('out.colMotivatedFor')}</th>
                    <th>{t('out.colTags')}</th>
                    <th>{t('out.colDescription')}</th>
                    {isAdmin && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {ungrouped.map((outcome) => (
                    <tr key={outcome.id}>
                      <td className="font-medium">{outcome.title}</td>
                      <td>{outcome.motivated_for}</td>
                      <td>
                        {outcome.tags.length > 0 ? (
                          <div className="flex gap-4" style={{ flexWrap: 'wrap' }}>
                            {outcome.tags.map((tag) => (
                              <span key={tag} className="badge">{tag}</span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="text-muted">
                        {outcome.description?.slice(0, 100)}
                        {outcome.description?.length > 100 ? '...' : ''}
                      </td>
                      {isAdmin && (
                        <td>
                          <div className="flex gap-8">
                            <button className="btn-icon" onClick={() => handleEdit(outcome)} title={t('common.edit')}>
                              <Pencil className="icon-sm" />
                            </button>
                            <button className="btn-icon" onClick={() => handleDelete(outcome)} title={t('common.delete')}>
                              <Trash2 className="icon-sm" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {showModal && (
        <Modal
          title={editingOutcome ? t('out.editOutcome') : t('out.addOutcome')}
          onClose={() => {
            setShowModal(false);
            setEditingOutcome(null);
          }}
        >
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">{t('out.labelTitle')}</label>
              <input
                type="text"
                className="form-input"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">{t('out.labelMotivatedBy')}</label>
              <input
                type="text"
                className="form-input"
                value={form.motivated_by}
                onChange={(e) => setForm({ ...form, motivated_by: e.target.value })}
                list="motivated-by-list"
              />
              <datalist id="motivated-by-list">
                {motivatedByValues.map((mb) => (
                  <option key={mb} value={mb} />
                ))}
              </datalist>
            </div>
            <div className="form-group">
              <label className="form-label">{t('out.labelMotivatedFor')}</label>
              <input
                type="text"
                className="form-input"
                value={form.motivated_for}
                onChange={(e) => setForm({ ...form, motivated_for: e.target.value })}
                list="motivated-for-list"
              />
              <datalist id="motivated-for-list">
                {motivatedForValues.map((mf) => (
                  <option key={mf} value={mf} />
                ))}
              </datalist>
            </div>
            <div className="form-group">
              <label className="form-label">{t('out.labelTags')}</label>
              <div className="form-tags">
                {form.tags.map((tag) => (
                  <span key={tag} className="form-tag">
                    {tag}
                    <button type="button" className="form-tag-x" onClick={() => removeFormTag(tag)}>&times;</button>
                  </span>
                ))}
                <input
                  ref={tagInputRef}
                  type="text"
                  className="form-tag-input"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); addFormTag(); }
                  }}
                  placeholder={form.tags.length === 0 ? t('out.tagPlaceholder') : ''}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('out.labelDescription')}</label>
              <textarea
                className="form-textarea"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <div className="modal-footer" style={{ padding: 0, border: 'none', marginTop: 'var(--s16)' }}>
              <button type="button" className="btn btn-ghost" onClick={() => setShowModal(false)}>
                {t('common.cancel')}
              </button>
              <button type="submit" className="btn btn-accent">
                {editingOutcome ? t('common.save') : t('out.create')}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {showUpload && (
        <Modal title={t('out.uploadModalTitle')} onClose={() => setShowUpload(false)}>
          <FileUpload
            expectedHeaders={['Title']}
            onUpload={handleUpload}
            onCancel={() => setShowUpload(false)}
          />
        </Modal>
      )}
    </div>
  );
}
